import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Save, Download, Loader2, Sun, BadgePercent, Lock, RefreshCw, History, RotateCcw } from "lucide-react";
import { getPreventivo, savePreventivo, downloadPreventivoPdf, downloadPreventivoStoricoPdf, regeneratePreventivo, restorePreventivo, fmtEur } from "@/lib/api";

const SEZIONI = ["IMPIANTO FOTOVOLTAICO", "PRODOTTI", "COSTI DI INSTALLAZIONE", "COSTI PRATICHE E ACCESSORI"];
const FASCE = [
  { id: "listino", label: "Listino (1ª fascia)" },
  { id: "scontato", label: "Scontato (2ª fascia)" },
  { id: "ultima", label: "Ultima offerta (3ª fascia — autorizzazione Direzione)" },
];
const num = (v) => (parseFloat(v) || 0);

const bandFor = (bands, kwp) => {
  const sorted = [...(bands || [])].sort((a, b) => num(a.max) - num(b.max));
  return sorted.find((b) => num(kwp) <= num(b.max)) || sorted[sorted.length - 1] || null;
};
const bandLabel = (b) => (!b ? "—" : num(b.max) >= 100000 ? `oltre ${num(b.min) - 1} kWp` : `${b.min}–${b.max} kWp`);

export const PreventivoEditor = ({ leadId, autoOpen = false }) => {
  const boxRef = useRef(null);
  const [prev, setPrev] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [listino, setListino] = useState({ bands: [], descrizione: "" });
  const [storico, setStorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { preventivo, catalog, listino_fv, storico } = await getPreventivo(leadId);
      setPrev(preventivo);
      setCatalog(catalog || []);
      setListino(listino_fv || { bands: [], descrizione: "" });
      setStorico(storico || []);
    } catch (e) {
      toast.error("Errore nel caricamento del preventivo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoOpen && !prev && !loading) {
      load();
      setTimeout(() => boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  const regenerate = async () => {
    if (!window.confirm("Archiviare il preventivo attuale e rigenerarlo dal listino (prezzo LISTINO, nuovo numero)? La versione precedente resta nello storico.")) return;
    setRegen(true);
    try {
      const d = await regeneratePreventivo(leadId);
      setPrev(d.preventivo);
      setStorico(d.storico || []);
      toast.success(`Preventivo rigenerato: n. ${d.preventivo.numero}`);
    } catch (e) {
      toast.error("Errore nella rigenerazione");
    } finally {
      setRegen(false);
    }
  };

  const restore = async (idx) => {
    if (!window.confirm("Ripristinare questa versione come preventivo corrente? Quella attuale verrà archiviata.")) return;
    try {
      const d = await restorePreventivo(leadId, idx);
      setPrev(d.preventivo);
      setStorico(d.storico || []);
      toast.success(`Ripristinato preventivo n. ${d.preventivo.numero}`);
    } catch (e) {
      toast.error("Errore nel ripristino");
    }
  };

  const fascia = prev?.fascia_listino || "listino";
  const priceFor = (kwp, f = fascia) => num(bandFor(listino.bands, kwp)?.[f]);
  const provvFor = (kwp, f = fascia) => num(bandFor(listino.bands, kwp)?.[`provv_${f}`]);

  const setField = (k, v) => setPrev((p) => ({ ...p, [k]: v }));
  const setPotenza = (v) => setPrev((p) => {
    const kwp = v === "" ? "" : Number(v);
    const voci = (p.voci || []).map((r) => (r.listino_fv && kwp !== "" && kwp > 0 ? { ...r, quantita: kwp, prezzo_unitario: priceFor(kwp) } : r));
    return { ...p, potenza_kwp: kwp, voci };
  });
  const setRow = (i, k, v) => setPrev((p) => {
    const voci = [...(p.voci || [])];
    voci[i] = { ...voci[i], [k]: v };
    const extra = {};
    if (voci[i].listino_fv && k === "quantita") {
      voci[i].prezzo_unitario = priceFor(v);
      if (num(v) > 0) extra.potenza_kwp = num(v);
    }
    return { ...p, voci, ...extra };
  });
  const setFascia = (f) => setPrev((p) => ({
    ...p, fascia_listino: f,
    voci: (p.voci || []).map((r) => (r.listino_fv ? { ...r, prezzo_unitario: priceFor(r.quantita, f) } : r)),
  }));
  const setDesc = (i, value) => setPrev((p) => {
    const voci = [...(p.voci || [])];
    const match = catalog.find((c) => (c.descrizione || "") === value);
    voci[i] = { ...voci[i], descrizione: value };
    if (match) {
      voci[i].prezzo_unitario = num(match.prezzo);
      if (match.unita) voci[i].unita = match.unita;
      if (match.sezione) voci[i].sezione = match.sezione;
    }
    return { ...p, voci };
  });
  const addRow = () => setPrev((p) => ({ ...p, voci: [...(p.voci || []), { sezione: "PRODOTTI", descrizione: "", quantita: 1, unita: "", prezzo_unitario: 0, iva_pct: p.iva_pct ?? 22 }] }));
  const addFromCatalog = (idx) => {
    const it = catalog[Number(idx)];
    if (!it) return;
    setPrev((p) => ({ ...p, voci: [...(p.voci || []), { sezione: it.sezione || "PRODOTTI", descrizione: it.descrizione || "", quantita: 1, unita: it.unita || "", prezzo_unitario: num(it.prezzo), iva_pct: p.iva_pct ?? 22 }] }));
  };
  const addListinoFv = () => {
    const kwp = num(prev?.potenza_kwp);
    if (kwp <= 0) { toast.error("Inserisci prima la potenza impianto (kWp)"); return; }
    const descr = (listino.descrizione || "").replace("{kwp}", String(kwp).replace(".", ","));
    setPrev((p) => ({ ...p, voci: [...(p.voci || []), { sezione: "IMPIANTO FOTOVOLTAICO", descrizione: descr, quantita: kwp, unita: "kWp", prezzo_unitario: priceFor(kwp), iva_pct: p.iva_pct ?? 22, listino_fv: true }] }));
  };
  const delRow = (i) => setPrev((p) => ({ ...p, voci: (p.voci || []).filter((_, j) => j !== i) }));

  const scontoPct = num(prev?.sconto_pct);
  const rowNet = (r) => num(r.quantita) * num(r.prezzo_unitario);
  const imponibile = (prev?.voci || []).reduce((a, r) => a + rowNet(r), 0);
  const nettoTot = imponibile * (1 - scontoPct / 100);
  const ivaGroups = {};
  (prev?.voci || []).forEach((r) => {
    const rate = num(r.iva_pct ?? prev?.iva_pct ?? 22);
    const net = rowNet(r) * (1 - scontoPct / 100);
    if (!ivaGroups[rate]) ivaGroups[rate] = { imp: 0, iva: 0 };
    ivaGroups[rate].imp += net;
    ivaGroups[rate].iva += net * rate / 100;
  });
  const ivaTot = Object.values(ivaGroups).reduce((a, g) => a + g.iva, 0);
  const totale = nettoTot + ivaTot;
  const ivaRatesSorted = Object.keys(ivaGroups).map(Number).sort((a, b) => b - a);

  // Provvigione agente (SOLO interna): % dal listino per fascia potenza × fascia prezzo, sull'imponibile FV
  const fvRows = (prev?.voci || []).filter((r) => r.listino_fv);
  const fvKwp = fvRows.reduce((a, r) => a + num(r.quantita), 0);
  const fvImp = fvRows.reduce((a, r) => a + rowNet(r), 0) * (1 - scontoPct / 100);
  const fvBand = bandFor(listino.bands, fvKwp);
  const provvPct = fvRows.length ? provvFor(fvKwp) : 0;
  const provvEur = fvImp * provvPct / 100;

  const payload = () => ({ ...prev, provvigione_pct: provvPct, provvigione_eur: Math.round(provvEur * 100) / 100 });

  const save = async () => {
    setSaving(true);
    try {
      await savePreventivo(leadId, payload());
      toast.success("Preventivo salvato");
      const d = await getPreventivo(leadId);
      setStorico(d.storico || []);
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    try {
      await savePreventivo(leadId, payload());
      await downloadPreventivoPdf(leadId, `preventivo_${(prev.numero || "").replace(/\//g, "-")}.pdf`);
    } catch (e) {
      toast.error("Errore nella generazione del PDF");
    }
  };

  return (
    <div className="card-dark rounded-2xl p-6" data-testid="preventivo-editor" ref={boxRef}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-heading text-lg text-white flex items-center gap-2"><FileText className="h-5 w-5 text-gold-400" /> Preventivo impianto fotovoltaico</h3>
        {!prev && (
          <Button onClick={load} disabled={loading} className="rounded-lg bg-gold-500 text-navy-900 hover:bg-gold-400" data-testid="preventivo-load-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} Genera / Modifica preventivo
          </Button>
        )}
        {prev && (
          <Button onClick={regenerate} disabled={regen} variant="outline" size="sm" className="rounded-lg border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="preventivo-regenerate-btn">
            {regen ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Rigenera da listino
          </Button>
        )}
      </div>

      {prev && storico.length > 0 && (
        <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="preventivo-storico">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-2"><History className="h-4 w-4 text-slate-400" /> Versioni precedenti ({storico.length})</div>
          <div className="space-y-1.5">
            {[...storico].reverse().map((v) => (
              <div key={v.idx} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 rounded-lg bg-white/[0.03] px-3 py-2" data-testid={`storico-row-${v.idx}`}>
                <span className="font-mono-plex text-white">n. {v.numero}</span>
                <span>{v.data}</span>
                <span className="uppercase tracking-wider">{v.fascia_listino}</span>
                {v.potenza_kwp ? <span>{v.potenza_kwp} kWp</span> : null}
                <span className="text-slate-200">Imponibile {fmtEur(v.imponibile)}</span>
                <span className="text-slate-500 italic">{v.motivo}{v.archiviato_il ? ` · ${new Date(v.archiviato_il).toLocaleDateString("it-IT")}` : ""}</span>
                <span className="ml-auto flex gap-1">
                  <button type="button" onClick={() => downloadPreventivoStoricoPdf(leadId, v.idx, `preventivo_${(v.numero || "").replace(/\//g, "-")}_archivio.pdf`)} className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-slate-200 hover:bg-white/5" data-testid={`storico-pdf-${v.idx}`}><Download className="h-3 w-3" /> PDF</button>
                  <button type="button" onClick={() => restore(v.idx)} className="inline-flex items-center gap-1 rounded-md border border-gold-500/30 px-2 py-1 text-gold-300 hover:bg-gold-500/10" data-testid={`storico-restore-${v.idx}`}><RotateCcw className="h-3 w-3" /> Ripristina</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {prev && (
        <div className="mt-5 space-y-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Numero" value={prev.numero} onChange={(e) => setField("numero", e.target.value)} testid="prev-numero" />
            <Field label="Data" value={prev.data} onChange={(e) => setField("data", e.target.value)} testid="prev-data" />
            <Field label="Valido fino al" value={prev.valido_fino} onChange={(e) => setField("valido_fino", e.target.value)} testid="prev-valido" />
            <div>
              <Label className="text-slate-400 text-xs mb-1 block">Potenza impianto (kWp)</Label>
              <Input type="number" min="0" step="0.1" value={prev.potenza_kwp ?? ""} onChange={(e) => setPotenza(e.target.value)} className="input-dark h-10" placeholder="es. 100" data-testid="prev-potenza-kwp" />
              <p className="text-[10px] text-slate-500 mt-1">Modificandola si aggiornano voce impianto, produzione, risparmio e rientro (pag. 2 del PDF) al salvataggio.</p>
            </div>
            <div>
              <Label className="text-slate-400 text-xs mb-1 block">IVA predefinita (nuove voci)</Label>
              <select value={prev.iva_pct ?? 22} onChange={(e) => setField("iva_pct", Number(e.target.value))} className="input-dark h-10 w-full rounded-md px-3" data-testid="prev-iva">
                <option value={22}>22%</option>
                <option value={10}>10%</option>
                <option value={4}>4%</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Cliente" value={prev.cliente_ragione_sociale} onChange={(e) => setField("cliente_ragione_sociale", e.target.value)} testid="prev-cliente" />
            <Field label="Indirizzo cliente / installazione" value={prev.cliente_indirizzo} onChange={(e) => setField("cliente_indirizzo", e.target.value)} testid="prev-indirizzo" />
            <div>
              <Label className="text-slate-400 text-xs mb-1 block">Richiesto da / Rivenditore <span className="text-slate-600">(interno, non sul PDF)</span></Label>
              <Input value={prev.richiesto_da ?? ""} onChange={(e) => setField("richiesto_da", e.target.value)} className="input-dark h-10" placeholder="es. Impresa Edile Rossi" data-testid="prev-richiesto-da" />
            </div>
          </div>

          {/* Listino FV B2B — fascia prezzo */}
          <div className="rounded-xl border border-gold-500/30 bg-gold-500/[0.06] p-4" data-testid="listino-fv-box">
            <div className="flex items-center gap-2 mb-3"><Sun className="h-4 w-4 text-gold-400" /><span className="text-sm font-semibold text-white">Listino FV B2B (impianti su tetto)</span></div>
            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-slate-400 text-xs mb-1 block">Fascia di prezzo riconosciuta al cliente</Label>
                <select value={fascia} onChange={(e) => setFascia(e.target.value)} className="input-dark h-10 w-full rounded-md px-3" data-testid="prev-fascia-listino">
                  {FASCE.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div className="text-sm text-slate-300" data-testid="prev-fascia-info">
                {fvRows.length ? (
                  <>Fascia potenza <b className="text-white">{bandLabel(fvBand)}</b> · <b className="text-gold-300">{fmtEur(priceFor(fvKwp))}/kWp</b> · {fvKwp} kWp</>
                ) : (
                  <>Nessuna voce da listino. Inserisci la potenza e aggiungi l'impianto.</>
                )}
              </div>
              <Button type="button" onClick={addListinoFv} size="sm" variant="outline" className="h-9 rounded-lg border-gold-500/40 text-gold-300 hover:bg-gold-500/10" data-testid="prev-add-listino-fv"><Plus className="h-4 w-4 mr-1" /> Aggiungi impianto FV da listino</Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Presentare sempre il prezzo di LISTINO. Lo SCONTATO è una leva di trattativa; l'ULTIMA OFFERTA è una condizione finale di chiusura da autorizzare dalla Direzione. Cambiando fascia il €/kWp della voce impianto si aggiorna automaticamente.</p>
          </div>

          <div className="space-y-2">
            <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 px-1">
              <span className="col-span-2">Sezione</span>
              <span className="col-span-2">Descrizione</span>
              <span className="col-span-2 text-right">Q.tà</span>
              <span className="col-span-1">Unità</span>
              <span className="col-span-2 text-right">Prezzo €</span>
              <span className="col-span-1 text-right">IVA</span>
              <span className="col-span-1 text-right">Totale</span>
              <span className="col-span-1" />
            </div>
            <datalist id="prev-catalog-datalist">
              {catalog.map((c, i) => <option key={i} value={c.descrizione}>{`${c.sezione} · € ${num(c.prezzo).toLocaleString("it-IT")}`}</option>)}
            </datalist>
            {(prev.voci || []).map((r, i) => (
              <div key={i} className={`grid grid-cols-12 gap-2 items-start ${r.listino_fv ? "rounded-lg bg-gold-500/[0.05] p-1" : ""}`} data-testid={`prev-row-${i}`}>
                <select value={r.sezione || "PRODOTTI"} onChange={(e) => setRow(i, "sezione", e.target.value)} className="input-dark h-9 col-span-6 md:col-span-2 rounded-md px-2 text-xs" data-testid={`prev-sez-${i}`}>
                  {SEZIONI.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {r.listino_fv ? (
                  <textarea value={r.descrizione ?? ""} onChange={(e) => setRow(i, "descrizione", e.target.value)} rows={4} className="input-dark col-span-6 md:col-span-2 rounded-md px-2 py-1.5 text-xs leading-snug" data-testid={`prev-desc-${i}`} />
                ) : (
                  <Input value={r.descrizione ?? ""} onChange={(e) => setDesc(i, e.target.value)} list="prev-catalog-datalist" className="input-dark h-9 col-span-6 md:col-span-2" placeholder="Voce (digita o scegli dal listino)" data-testid={`prev-desc-${i}`} />
                )}
                <Input type="number" min="0" step="0.01" value={r.quantita ?? ""} onChange={(e) => setRow(i, "quantita", e.target.value)} className="input-dark h-9 col-span-3 md:col-span-2 text-right" data-testid={`prev-qta-${i}`} />
                <Input value={r.unita ?? ""} onChange={(e) => setRow(i, "unita", e.target.value)} className="input-dark h-9 col-span-3 md:col-span-1" data-testid={`prev-unita-${i}`} />
                <Input type="number" min="0" step="0.01" value={r.prezzo_unitario ?? ""} onChange={(e) => setRow(i, "prezzo_unitario", e.target.value)} className="input-dark h-9 col-span-3 md:col-span-2 text-right" data-testid={`prev-prezzo-${i}`} />
                <select value={r.iva_pct ?? prev.iva_pct ?? 22} onChange={(e) => setRow(i, "iva_pct", Number(e.target.value))} className="input-dark h-9 col-span-3 md:col-span-1 rounded-md px-1 text-xs text-right" data-testid={`prev-iva-${i}`}>
                  <option value={22}>22%</option>
                  <option value={10}>10%</option>
                  <option value={4}>4%</option>
                </select>
                <span className="col-span-2 md:col-span-1 text-right text-slate-200 font-mono-plex text-xs pt-2">{fmtEur(num(r.quantita) * num(r.prezzo_unitario))}</span>
                <button type="button" onClick={() => delRow(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex justify-end pt-2" data-testid={`prev-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <select value="" onChange={(e) => { addFromCatalog(e.target.value); e.target.value = ""; }} className="input-dark h-8 rounded-lg px-2 text-xs max-w-[22rem]" data-testid="prev-catalog-select">
                <option value="">+ Aggiungi da listino…</option>
                {SEZIONI.map((sez) => {
                  const items = catalog.map((c, i) => ({ c, i })).filter(({ c }) => (c.sezione || "PRODOTTI") === sez);
                  if (!items.length) return null;
                  return (
                    <optgroup key={sez} label={sez}>
                      {items.map(({ c, i }) => (
                        <option key={i} value={i}>{c.descrizione} — € {num(c.prezzo).toLocaleString("it-IT")}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              <Button type="button" onClick={addRow} size="sm" variant="outline" className="h-8 rounded-lg border-gold-500/40 text-gold-300 hover:bg-gold-500/10" data-testid="prev-add"><Plus className="h-4 w-4 mr-1" /> Voce vuota</Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-4 border-t border-white/10 pt-4">
            {/* Provvigione agente — SOLO INTERNA */}
            <div className="sm:w-80 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4 self-start" data-testid="prev-provvigione">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><BadgePercent className="h-4 w-4" /> Provvigione agente</div>
              {fvRows.length ? (
                <>
                  <div className="mt-2 flex justify-between text-sm text-slate-300"><span>Fascia {bandLabel(fvBand)} · {FASCE.find((f) => f.id === fascia)?.label.split(" (")[0]}</span><span className="font-mono-plex text-white" data-testid="prev-provvigione-pct">{provvPct}%</span></div>
                  <div className="mt-1 flex justify-between text-sm text-slate-400"><span>su imponibile FV {fmtEur(fvImp)}</span><span className="font-mono-plex font-bold text-emerald-300 text-base" data-testid="prev-provvigione-eur">{fmtEur(provvEur)}</span></div>
                </>
              ) : (
                <p className="mt-2 text-xs text-slate-500">Nessuna voce impianto da listino: provvigione non calcolabile.</p>
              )}
              <p className="mt-2 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Lock className="h-3 w-3" /> Solo interno · non compare sul PDF cliente</p>
            </div>
            <div className="sm:w-72 space-y-1 text-sm">
              <Rowt k="Imponibile" v={fmtEur(nettoTot)} />
              {ivaRatesSorted.map((rate) => (
                <Rowt key={rate} k={`IVA ${rate}% (su ${fmtEur(ivaGroups[rate].imp)})`} v={fmtEur(ivaGroups[rate].iva)} />
              ))}
              <div className="flex justify-between font-semibold text-gold-400 text-base pt-1"><span>TOTALE</span><span className="font-mono-plex" data-testid="prev-totale">{fmtEur(totale)}</span></div>
              {num(prev.potenza_kwp) > 0 && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gold-500/20 text-white" data-testid="prev-costo-kw">
                  <span className="text-slate-300">Impianto <b>{num(prev.potenza_kwp)} kWp</b> · Costo a kW</span>
                  <span className="font-mono-plex font-bold text-gold-300">{fmtEur(nettoTot / num(prev.potenza_kwp))}</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Pagamento" value={prev.pagamento} onChange={(e) => setField("pagamento", e.target.value)} testid="prev-pagamento" />
            <Field label="Note" value={prev.note} onChange={(e) => setField("note", e.target.value)} testid="prev-note" />
          </div>

          <div className="flex flex-wrap gap-3 justify-end items-center">
            <Link to="/admin/preventivi" className="text-xs text-slate-400 hover:text-gold-300 inline-flex items-center gap-1 mr-auto" data-testid="preventivo-archivio-link"><FileText className="h-3.5 w-3.5" /> Vai all'archivio preventivi</Link>
            <Button onClick={save} disabled={saving} variant="outline" className="rounded-lg border-white/15 text-slate-200 hover:bg-white/5" data-testid="preventivo-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva preventivo
            </Button>
            <Button onClick={download} className="rounded-lg bg-gold-500 text-navy-900 hover:bg-gold-400" data-testid="preventivo-pdf-btn">
              <Download className="h-4 w-4 mr-1.5" /> Scarica PDF preventivo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-slate-400 text-xs mb-1 block">{label}</Label>
    <Input value={value ?? ""} onChange={onChange} className="input-dark h-10" data-testid={testid} />
  </div>
);

const Rowt = ({ k, v }) => (
  <div className="flex justify-between text-slate-400"><span>{k}</span><span className="font-mono-plex text-slate-200">{v}</span></div>
);
